import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, Trash2, Waves } from "lucide-react";
import bladderDiaryIcon from "@/../public/bladder-diary-icon.png";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface BladderEntry {
  id: string;
  log_date: string;
  log_time: string;
  entry_type: string;
  fluid_amount_ml?: number | null;
  fluid_type?: string | null;
  urgency_level?: number | null;
  had_leakage?: boolean | null;
  leakage_amount?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
}

export const BladderDiary = () => {
  const { toast } = useToast();
  // Always use today's date for logging in the Today tab
  const selectedDate = format(new Date(), 'yyyy-MM-dd');
  const [entries, setEntries] = useState<BladderEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Intake form state
  const [intakeTime, setIntakeTime] = useState(format(new Date(), 'HH:mm'));
  const [fluidAmount, setFluidAmount] = useState('');
  const [fluidType, setFluidType] = useState('water');
  
  // Urination form state
  const [urinationTime, setUrinationTime] = useState(format(new Date(), 'HH:mm'));
  const [urgency, setUrgency] = useState('3');
  const [hadLeakage, setHadLeakage] = useState(false);
  const [leakageAmount, setLeakageAmount] = useState('small');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchEntries();
  }, [selectedDate]);

  const fetchEntries = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('bladder_diary')
        .select('*')
        .eq('user_id', user.id)
        .eq('log_date', selectedDate)
        .order('log_time', { ascending: true });

      if (error) throw error;
      setEntries(data || []);
    } catch (error: any) {
      console.error('Error fetching entries:', error);
    }
  };

  const logIntake = async () => {
    if (!fluidAmount) {
      toast({ title: "Please enter fluid amount", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from('bladder_diary').insert({
        user_id: user.id,
        log_date: selectedDate,
        log_time: intakeTime,
        entry_type: 'intake',
        fluid_amount_ml: parseInt(fluidAmount),
        fluid_type: fluidType,
      });

      if (error) throw error;

      toast({ title: "Fluid intake logged successfully" });
      setFluidAmount('');
      fetchEntries();
    } catch (error: any) {
      toast({ 
        title: "Error logging intake", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const logUrination = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from('bladder_diary').insert({
        user_id: user.id,
        log_date: selectedDate,
        log_time: urinationTime,
        entry_type: 'urination',
        urgency_level: parseInt(urgency),
        had_leakage: hadLeakage,
        leakage_amount: hadLeakage ? leakageAmount : null,
        notes: notes || null,
      });

      if (error) throw error;

      toast({ title: "Bathroom visit logged successfully" });
      setUrgency('3');
      setHadLeakage(false);
      setNotes('');
      fetchEntries();
    } catch (error: any) {
      toast({ 
        title: "Error logging urination", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id: string) => {
    try {
      const { error } = await supabase
        .from('bladder_diary')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: "Entry deleted" });
      fetchEntries();
    } catch (error: any) {
      toast({ 
        title: "Error deleting entry", 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  return (
    <Card className="border border-border/60 rounded-2xl shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border/60">
            <img src={bladderDiaryIcon} alt="" className="h-full w-full object-cover" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">A quiet record</p>
            <CardTitle className="font-serif text-2xl leading-tight font-normal">
              Bladder <em className="italic">diary</em>
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="intake" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="intake">Log Intake</TabsTrigger>
            <TabsTrigger value="urination">Log Urination</TabsTrigger>
          </TabsList>

          <TabsContent value="intake" className="space-y-4">
            <div>
              <Label htmlFor="intake-time">Time</Label>
              <Input
                id="intake-time"
                type="time"
                value={intakeTime}
                onChange={(e) => setIntakeTime(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="fluid-amount">Amount (ml)</Label>
              <Input
                id="fluid-amount"
                type="number"
                placeholder="250"
                value={fluidAmount}
                onChange={(e) => setFluidAmount(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="fluid-type">Type of Fluid</Label>
              <Select value={fluidType} onValueChange={setFluidType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="water">Water</SelectItem>
                  <SelectItem value="tea">Tea</SelectItem>
                  <SelectItem value="coffee">Coffee</SelectItem>
                  <SelectItem value="juice">Juice</SelectItem>
                  <SelectItem value="soda">Soda</SelectItem>
                  <SelectItem value="alcohol">Alcohol</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={logIntake} disabled={loading} className="w-full">
              Log Intake
            </Button>
          </TabsContent>

          <TabsContent value="urination" className="space-y-4">
            <div>
              <Label htmlFor="urination-time">Time</Label>
              <Input
                id="urination-time"
                type="time"
                value={urinationTime}
                onChange={(e) => setUrinationTime(e.target.value)}
              />
            </div>

            <div>
              <Label>Urgency Level (1 = None, 5 = Extreme)</Label>
              <RadioGroup value={urgency} onValueChange={setUrgency}>
                <div className="flex gap-4">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <RadioGroupItem value={level.toString()} id={`urgency-${level}`} />
                      <Label htmlFor={`urgency-${level}`}>{level}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="had-leakage"
                checked={hadLeakage}
                onChange={(e) => setHadLeakage(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="had-leakage">Leakage occurred</Label>
            </div>

            {hadLeakage && (
              <div>
                <Label htmlFor="leakage-amount">Leakage Amount</Label>
                <Select value={leakageAmount} onValueChange={setLeakageAmount}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (a few drops)</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Activity, triggers, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <Button onClick={logUrination} disabled={loading} className="w-full">
              Log Bathroom Visit
            </Button>
          </TabsContent>
        </Tabs>

        {/* Entries List */}
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Today's Entries {entries.length > 0 && `(${entries.length})`}
          </h3>
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No entries for this date</p>
          ) : (
            <div className="space-y-2">
              {entries.map((entry) => (
                <div key={entry.id} className={`p-3 border rounded-lg flex justify-between items-start ${
                  entry.entry_type === 'intake' 
                    ? 'bg-blue-500/5 border-blue-500/20' 
                    : 'bg-amber-500/5 border-amber-500/20'
                }`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span>{entry.log_time}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        entry.entry_type === 'intake' 
                          ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' 
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      }`}>
                        {entry.entry_type}
                      </span>
                    </div>
                    {entry.entry_type === 'intake' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {entry.fluid_amount_ml}ml {entry.fluid_type}
                      </p>
                    )}
                    {entry.entry_type === 'urination' && (
                      <div className="text-sm text-muted-foreground mt-1 space-y-1">
                        <p>Urgency: {entry.urgency_level}/5</p>
                        {entry.had_leakage && (
                          <p className="text-accent">Leakage: {entry.leakage_amount}</p>
                        )}
                        {entry.notes && <p className="italic text-xs">"{entry.notes}"</p>}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => deleteEntry(entry.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};